import { useId, type ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  label: string;
  /**
   * Recebe o `id` a aplicar no controle e os atributos de acessibilidade já resolvidos.
   * É função, e não `ReactNode`, porque o vínculo entre rótulo, erro e controle só existe
   * se o controle receber estes atributos — deixá-lo a cargo de quem chama é garantir que
   * um dia alguém esqueça, e o leitor de tela anuncie um campo sem nome nem erro.
   */
  children: (field: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
  }) => ReactNode;
  error?: string;
  hint?: string;
  required?: boolean;
  /**
   * Rótulo só para leitor de tela: campo dentro de uma linha de lista, onde a própria linha diz o
   * que se digita e um rótulo visível repetido em cada item vira ruído.
   */
  hideLabel?: boolean;
  className?: string;
}

/**
 * Rótulo + controle + mensagem, para o tratamento de erro ser uniforme em vez de
 * repetido campo a campo.
 *
 * O erro substitui a dica quando existe, e não se soma a ela: duas linhas embaixo do
 * campo, uma dizendo como preencher e outra dizendo que está errado, competem pela mesma
 * atenção no momento em que a pessoa já errou.
 */
export function FormField({
  label,
  children,
  error,
  hint,
  required = false,
  hideLabel = false,
  className,
}: FormFieldProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const hasMessage = Boolean(error ?? hint);

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} className={cn(hideLabel && 'sr-only')}>
        {label}
        {required && (
          <>
            <span aria-hidden className="ml-0.5 text-destructive">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </Label>

      {children({
        id,
        'aria-describedby': hasMessage ? messageId : undefined,
        'aria-invalid': error ? true : undefined,
      })}

      {hasMessage && (
        <p
          id={messageId}
          // `role="alert"` só no erro: a dica está lá desde que a tela abriu, e anunciá-la
          // como alerta interromperia a leitura do formulário sem que nada tenha
          // acontecido.
          role={error ? 'alert' : undefined}
          className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
